import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Rss, ExternalLink, Calendar, ChevronRight, Beaker, ArrowRight, Sparkles } from 'lucide-react';
import { getPublishedNews, markNewsRead } from '../utils/api';
import SaveToIdeasButton from '../components/SaveToIdeasButton';

const NewsPage = () => {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const firstNewRef = useRef(null);

  useEffect(() => {
    const fetchNews = async () => {
      try {
        // Fetch FIRST (server computes is_new from the current last-viewed time)...
        const data = await getPublishedNews();
        setArticles(data);
        // ...THEN mark as read and clear the sidebar badge
        try {
          await markNewsRead();
          window.dispatchEvent(new Event('news-marked-read'));
        } catch (e) {
          console.error('Failed to mark news as read:', e);
        }
      } catch (error) {
        console.error('Failed to load news:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchNews();
  }, []);

  // Scroll to / highlight the newly added items once loaded
  useEffect(() => {
    if (!loading && firstNewRef.current) {
      const timer = setTimeout(() => {
        firstNewRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [loading]);

  const firstNewId = articles.find(a => a.is_new)?.id;

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <Rss className="h-8 w-8 text-orange-500" />
          <h1 className="text-2xl sm:text-4xl font-bold text-slate-900">CLL News</h1>
        </div>
        <p className="text-slate-600">
          Curated news and updates from trusted blood cancer organizations
        </p>
      </div>

      {/* Featured Research - Pinned at top */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-3">
          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
            Featured Research
          </span>
        </div>
        <Link
          to="/green-tea-research"
          className="block bg-gradient-to-r from-green-50 via-emerald-50 to-teal-50 border border-green-200 rounded-2xl p-5 sm:p-6 hover:shadow-lg hover:border-green-300 transition-all group"
          data-testid="featured-research-news"
        >
          <div className="flex items-start gap-4">
            <div className="p-3 bg-green-100 rounded-xl group-hover:bg-green-200 transition-colors hidden sm:block">
              <Beaker className="h-8 w-8 text-green-600" />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <span className="flex items-center gap-1 bg-green-100 text-green-700 px-2 py-0.5 rounded-full text-xs">
                  <Beaker className="h-3 w-3" />
                  Mayo Clinic Study
                </span>
              </div>
              <h2 className="text-lg sm:text-xl font-bold text-slate-900 group-hover:text-green-700 transition-colors mb-2">
                Green Tea Extract (EGCG) Shows Promise in CLL Clinical Trial
              </h2>
              <p className="text-slate-600 text-sm sm:text-base mb-3">
                Mayo Clinic research found 69% of early-stage CLL patients had a biological response to EGCG, with significant reductions in lymphocyte counts and lymph node size.
              </p>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="text-green-700 font-semibold">69% response rate</span>
                <span className="text-slate-300 hidden sm:inline">•</span>
                <span className="text-slate-600">42 patients</span>
                <span className="text-slate-300 hidden sm:inline">•</span>
                <span className="text-slate-600">Low toxicity</span>
              </div>
            </div>
            <ArrowRight className="h-6 w-6 text-green-500 group-hover:translate-x-1 transition-transform flex-shrink-0" />
          </div>
        </Link>
      </div>

      {/* News Grid */}
      {articles.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 p-8 text-center">
          <Rss className="h-12 w-12 mx-auto mb-4 text-slate-300" />
          <p className="text-slate-600">No news articles yet</p>
          <p className="text-sm text-slate-500 mt-2">Check back soon for curated CLL news and updates</p>
        </div>
      ) : (
        <div className="grid gap-6">
          {articles.map(article => (
            <article 
              key={article.id} 
              ref={article.id === firstNewId ? firstNewRef : null}
              data-testid={article.is_new ? 'news-item-new' : 'news-item'}
              className={`bg-white rounded-2xl border overflow-hidden hover:shadow-lg transition-shadow ${
                article.is_new ? 'border-amber-300 ring-2 ring-amber-200/60' : 'border-slate-200'
              }`}
            >
              <div className="flex flex-col sm:flex-row">
                {article.featured_image && (
                  <div className="sm:w-64 flex-shrink-0">
                    <img 
                      src={article.featured_image} 
                      alt={article.title}
                      className="w-full h-48 sm:h-full object-cover"
                    />
                  </div>
                )}
                <div className="flex-1 p-5 sm:p-6">
                  <div className="flex items-center gap-2 text-xs text-slate-500 mb-3 flex-wrap">
                    {article.is_new && (
                      <span className="flex items-center gap-1 bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-semibold" data-testid="news-new-chip">
                        <Sparkles className="h-3 w-3" />
                        New
                      </span>
                    )}
                    <span className="flex items-center gap-1 bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full">
                      <Rss className="h-3 w-3" />
                      {article.source_name}
                    </span>
                    {(article.original_published_date || article.published_at) && (
                      <span className="flex items-center gap-1" title="Original publish date">
                        <Calendar className="h-3 w-3" />
                        {formatDate(article.original_published_date || article.published_at)}
                      </span>
                    )}
                  </div>
                  
                  <h2 className="text-lg sm:text-xl font-semibold text-slate-900 mb-2 line-clamp-2">
                    {article.title}
                  </h2>
                  
                  <p className="text-slate-600 text-sm sm:text-base mb-4 line-clamp-3">
                    {article.excerpt}
                  </p>
                  
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Link
                      to={`/news/${article.slug}`}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
                    >
                      Read Summary
                      <ChevronRight className="h-4 w-4" />
                    </Link>
                    <a
                      href={article.source_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 px-4 py-2 border border-slate-200 text-slate-700 rounded-lg text-sm font-medium hover:bg-slate-50 transition-colors"
                    >
                      <ExternalLink className="h-4 w-4" />
                      Read Original
                    </a>
                    <SaveToIdeasButton
                      title={article.title}
                      category="CLL News"
                      sourcePage="/news"
                      sourceUrl={`/news/${article.slug}`}
                      variant="compact"
                    />
                  </div>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
};

export default NewsPage;
